import React, { useEffect, useState } from "react";
import { getLivePublicMatches } from "../store/supabaseMatches";
import PublicLayout from "../components/public/PublicLayout";
import { MatchCard, MatchCardSkeleton, type PublicMatch } from "../components/public/MatchCard";

function isAbortError(err: unknown): boolean {
  return err instanceof Error && (err.name === "AbortError" || err.message === "signal is aborted without reason");
}

export default function FixturesPage() {
  const [matches, setMatches] = useState<PublicMatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchFixtures = async () => {
      try {
        const data = await getLivePublicMatches();
        if (!mounted) return;
        setMatches((data || []).filter((m: PublicMatch) => m.status === "upcoming"));
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        if (isAbortError(err)) {
          console.log("[FixturesPage] Fetch aborted on unmount");
          return;
        }
        console.error("[FixturesPage] Error fetching fixtures:", err);
        setLoading(false);
      }
    };

    fetchFixtures();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <PublicLayout activePath="/fixtures" showBottomNav>
      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="section-panel-upcoming rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-3 h-3 bg-emerald-500 rounded-full shadow-lg shadow-emerald-500/40" />
            <h1 className="text-2xl font-black text-white uppercase tracking-wide">Fixtures</h1>
            {!loading && matches.length > 0 && (
              <span className="ml-auto bg-emerald-500/20 text-emerald-400 text-xs font-bold px-2.5 py-1 rounded-full border border-emerald-500/30">
                {matches.length} upcoming
              </span>
            )}
          </div>

          {loading ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <MatchCardSkeleton />
              <MatchCardSkeleton />
              <MatchCardSkeleton />
            </div>
          ) : matches.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {matches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  onClick={() => { window.location.href = `/match/${match.id}`; }}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-7xl mb-4">📅</div>
              <p className="text-slate-300 text-xl font-semibold mb-2">No upcoming fixtures</p>
              <p className="text-slate-400/70 text-sm mb-6">Fixtures will appear here once scheduled</p>
              <button
                onClick={() => { window.location.href = "/live"; }}
                className="bg-emerald-600/80 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-all"
              >
                View Live Matches
              </button>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
